import type { AreaFrameUpdateData } from "@situate/contracts";
import { LIVE_HEATMAP_CONFIG } from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import { mapTrackPositionsToSchematicSpace } from "@/features/deployment-dashboard/live-view/utils/map-track-positions-to-schematic";
import type { LiveTrackPoint } from "@/features/deployment-dashboard/types";
import type { WorldToPixelTransformAsset } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";

/**
 * Flattens the latest per-area live-tracks frames into a single list of schematic
 * points, one per currently tracked object (keyed by `area_id` and `object_id`),
 * mapped into the facility schematic's pixel coordinate space.
 *
 * Area qualification prevents two objects reported by different areas from sharing
 * an ECharts datum identity. Returns no markers while `transform` hasn't loaded yet
 * (see `useFacilitySchematicAssets`).
 */
export function deriveLiveTrackMarkers(
  transform: Pick<WorldToPixelTransformAsset, "matrix"> | undefined,
  latestAreaFrames: Record<string, AreaFrameUpdateData>
): LiveTrackPoint[] {
  if (!transform) return [];

  const rawPoints = Object.values(latestAreaFrames).flatMap((frame) =>
    frame.objects.map((object) => ({
      id: `${encodeURIComponent(frame.area_id)}:${encodeURIComponent(object.object_id)}`,
      areaId: frame.area_id,
      objectId: object.object_id,
      x: object.position.x,
      y: object.position.y,
    }))
  );

  return mapTrackPositionsToSchematicSpace(
    transform,
    rawPoints,
    LIVE_HEATMAP_CONFIG.pointRadiusInMeters
  );
}
